/**
 * Who sits in the faces row of a big room. The row has fewer seats than the
 * room has people, so it chooses who matters now, in this order:
 *   1. the host, always first, talking or not;
 *   2. whoever the host called on or put in the spotlight;
 *   3. whoever is speaking now, then the last few voices, the latest first;
 *   4. hands up, in the order they went up;
 *   5. the rest with their camera on, those the kit sends a picture for first.
 * Your own picture never takes a seat. Cameras only fill empty seats; the
 * first four rules are what move a face out. A seat is kept for a few seconds
 * at least, a voice has to last a moment before it takes one (a cough swaps
 * nobody in), and when a new voice needs a seat, the face heard least recently
 * gives it up while everyone else stays where they sit.
 *
 * Every call is one pass over the room, so a thousand people cost nothing a
 * page would feel; only the few voices and hands are ever sorted.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { MeetingFace } from "../../live/meeting/meeting";

/** The shortest time a face keeps its seat once it has one. */
export const HOLD_MS = 4000;
/** How long a voice has to last before it takes a seat. */
export const DWELL_MS = 1500;

export interface Voice {
  id: string;
  /** When they started being the one heard (ms). */
  since: number;
}

export interface LineupInput {
  /** Everyone at the table, your own face among them. */
  faces: readonly MeetingFace[];
  /** Who is heard now, and since when. */
  speaking: Voice | null;
  /** When each voice that lasted was last heard (ms). */
  heard: ReadonlyMap<string, number>;
  /** Tiles for everyone but you, the "+N more" tile among them. */
  seats: number;
  now: number;
}

export interface Lineup {
  /** The faces on screen, host first, each in the seat it keeps. */
  ids: string[];
  /** The same faces from the one that matters most, for a row with room for only a few. */
  ranked: string[];
  /** When each face on screen took its seat. */
  since: ReadonlyMap<string, number>;
  /** Everyone else in the room, but you: the "+N more". */
  more: number;
  /** When the line-up could change with nothing new happening (a voice lasting, a seat freeing), or null. */
  wakeAt: number | null;
}

/** A voice that has stopped is remembered when it lasted long enough to count. */
export function noteVoice(heard: Map<string, number>, ended: Voice | null, now: number): void {
  if (ended && now - ended.since >= DWELL_MS) heard.set(ended.id, now);
}

/** The latest voices to keep on screen: a third of the seats. */
function recentShare(cap: number): number {
  return Math.max(1, Math.floor(cap / 3));
}

export function lineUp(prev: Lineup | null, input: LineupInput): Lineup {
  const { faces, speaking, heard, seats, now } = input;
  const cap = Math.max(1, seats - 1);
  const present = new Map<string, MeetingFace>();
  const hosts: string[] = [];
  const pinned: string[] = [];
  const raised: MeetingFace[] = [];
  const seen: string[] = [];
  const cameras: string[] = [];
  const others: string[] = [];
  // One pass sorts the room into what each rule asks for.
  for (const face of faces) {
    if (face.mine) continue;
    present.set(face.id, face);
    if (face.host) hosts.push(face.id);
    if (face.pinned) pinned.push(face.id);
    if (face.handAt !== null) raised.push(face);
    if (face.cameraOn && face.video) seen.push(face.id);
    else if (face.cameraOn) cameras.push(face.id);
    else others.push(face.id);
  }
  const wasSeated = new Set(prev?.ids ?? []);
  let wakeAt: number | null = null;
  const wake = (at: number) => {
    wakeAt = wakeAt === null ? at : Math.min(wakeAt, at);
  };

  // A voice counts once it has lasted, or at once for someone already on screen.
  let voice: string | null = null;
  if (speaking && present.has(speaking.id)) {
    if (now - speaking.since >= DWELL_MS || wasSeated.has(speaking.id)) voice = speaking.id;
    else wake(speaking.since + DWELL_MS);
  }
  const recent = [...heard.entries()]
    .filter(([id]) => present.has(id) && id !== voice)
    .toSorted((a, b) => b[1] - a[1])
    .slice(0, recentShare(cap))
    .map(([id]) => id);
  const hands = raised.toSorted((a, b) => (a.handAt ?? 0) - (b.handAt ?? 0)).map((f) => f.id);

  const ranked: string[] = [];
  const rank = new Map<string, number>();
  const offer = (id: string) => {
    if (!rank.has(id)) {
      rank.set(id, ranked.length);
      ranked.push(id);
    }
  };
  for (const id of hosts) offer(id);
  for (const id of pinned) offer(id);
  if (voice) offer(voice);
  for (const id of recent) offer(id);
  for (const id of hands) offer(id);
  // Anyone ranked from here on only fills an empty seat: cameras never push a face out.
  const fillFrom = ranked.length;
  // The fill: enough to choose from, never the whole room.
  for (const pool of [seen, cameras, others]) {
    for (const id of pool) {
      if (ranked.length >= cap * 2) break;
      offer(id);
    }
  }
  const mustStay = new Set<string>([...hosts, ...pinned, ...hands]);
  if (voice) mustStay.add(voice);
  const rankOf = (id: string) => rank.get(id) ?? Number.POSITIVE_INFINITY;

  // Everyone keeps the seat they had, while they are still here.
  const ids = (prev?.ids ?? []).filter((id) => present.has(id));
  const since = new Map<string, number>();
  for (const id of ids) since.set(id, prev?.since.get(id) ?? now);
  // A smaller screen than before gives up the seats that matter least.
  while (ids.length > cap) {
    let worst = ids.length - 1;
    for (let i = 0; i < ids.length; i++) if (rankOf(ids[i]) > rankOf(ids[worst])) worst = i;
    since.delete(ids[worst]);
    ids.splice(worst, 1);
  }
  const seated = new Set(ids);
  const leaving = new Set<string>();

  for (const id of ranked) {
    if (seated.has(id) || leaving.has(id)) continue;
    if (ids.length < cap) {
      ids.push(id);
      seated.add(id);
      since.set(id, now);
      continue;
    }
    if (rankOf(id) >= fillFrom) continue;
    // The seat goes from the face heard least recently among those free to go.
    let out = -1;
    for (let i = 0; i < ids.length; i++) {
      const sitting = ids[i];
      if (mustStay.has(sitting) || rankOf(sitting) <= rankOf(id)) continue;
      const took = since.get(sitting) ?? now;
      if (now - took < HOLD_MS) {
        wake(took + HOLD_MS);
        continue;
      }
      if (out < 0) {
        out = i;
        continue;
      }
      const a = heard.get(sitting) ?? Number.NEGATIVE_INFINITY;
      const b = heard.get(ids[out]) ?? Number.NEGATIVE_INFINITY;
      if (a < b || (a === b && rankOf(sitting) > rankOf(ids[out]))) out = i;
    }
    if (out < 0) continue;
    seated.delete(ids[out]);
    since.delete(ids[out]);
    leaving.add(ids[out]);
    ids[out] = id;
    seated.add(id);
    since.set(id, now);
  }

  // The host is always first; anyone else stays where they sit.
  for (const host of hosts.toReversed()) {
    const at = ids.indexOf(host);
    if (at > 0) {
      ids.splice(at, 1);
      ids.unshift(host);
    }
  }
  return {
    ids,
    ranked: ids.toSorted((a, b) => rankOf(a) - rankOf(b)),
    since,
    more: present.size - ids.length,
    wakeAt,
  };
}

/**
 * The faces for the row: seated by the rules above, and the same array until the room, the voice or the
 * seat count changes, so a chat line or a reaction draws no face again.
 * `seats` is the faces the row holds; the host and the one at the front are never in it.
 */
export function useLineup(faces: readonly MeetingFace[], speakingId: string | null, seats: number, skip: string | null): MeetingFace[] {
  const voice = useRef<Voice | null>(null);
  const heard = useRef(new Map<string, number>());
  const last = useRef<Lineup | null>(null);
  const [due, setDue] = useState(0);
  if ((voice.current?.id ?? null) !== speakingId) {
    const now = Date.now();
    noteVoice(heard.current, voice.current, now);
    voice.current = speakingId ? { id: speakingId, since: now } : null;
  }
  const speaking = voice.current;
  // Why due is a dependency: a voice lasting or a seat freeing changes the row with nothing else happening.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  const { up, shown } = useMemo(() => {
    const room = faces.filter((f) => !f.host && f.id !== skip);
    const up = lineUp(last.current, { faces: room, speaking, heard: heard.current, seats: seats + 1, now: Date.now() });
    last.current = up;
    const byId = new Map(room.map((f) => [f.id, f]));
    return { up, shown: up.ids.flatMap((id) => byId.get(id) ?? []) };
  }, [faces, skip, speaking, seats, due]);
  const wakeAt = up.wakeAt;
  useEffect(() => {
    if (wakeAt === null) return;
    const timer = window.setTimeout(() => setDue((n) => n + 1), Math.max(0, wakeAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [wakeAt]);
  return shown;
}
